import { useState, useEffect } from "react";
import {
  ArrowLeft,
  Lock,
  Shield,
  Bell,
  Eye,
  EyeOff,
  CheckSquare,
  Square,
} from "lucide-react";

export default function ExpertPrivacySecuritySettings() {
  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: "public",
    show_location: true,
    show_email: true,
    show_phone: true,
    show_consultation_fee: true,
    show_availability: true,
    two_factor_authentication: false,
    login_notifications: true,
    consultation_notifications: true,
    data_encryption: true,
  });

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Fetch privacy settings on component mount
  useEffect(() => {
    fetchPrivacySettings();
  }, []);

  const fetchPrivacySettings = async () => {
    try {
      setLoading(true);
      // Simulate API call - replace with actual expert API
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      setError("Failed to load privacy settings");
      showErrorAlertMessage("Failed to load privacy settings");
      setLoading(false);
    }
  };

  // Toggle boolean settings
  const toggleSetting = (settingName) => {
    setPrivacySettings({
      ...privacySettings,
      [settingName]: !privacySettings[settingName],
    });
  };

  // Handle profile visibility change
  const handleVisibilityChange = (e) => {
    setPrivacySettings({
      ...privacySettings,
      profile_visibility: e.target.value,
    });
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
    // Clear any previous messages when user starts typing
    setErrorMessage("");
    setSuccessMessage("");
  };

  // Submit password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      showErrorAlertMessage("All password fields are required");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorAlertMessage("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showErrorAlertMessage("Password must be at least 8 characters long");
      return;
    }

    try {
      setSavingPassword(true);
      
      // API call to update expert password
      const response = await fetch('/api/expert/update-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          confirm_password: passwordData.confirmPassword
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update password');
      }

      const result = await response.json();
      
      if (result.success) {
        showSuccessAlertMessage("Password changed successfully");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        throw new Error(result.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showErrorAlertMessage("Failed to change password. Please try again.");
    } finally {
      setSavingPassword(false);
    }
  };

  // Show success alert
  const showSuccessAlertMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
      setSuccessMessage("");
    }, 4000);
  };

  // Show error alert
  const showErrorAlertMessage = (message) => {
    setErrorMessage(message);
    setShowErrorAlert(true);
    setTimeout(() => {
      setShowErrorAlert(false);
      setErrorMessage("");
    }, 4000);
  };

  // Save all privacy and security settings
  const saveAllSettings = async () => {
    try {
      setSavingPrivacy(true);
      
      // API call to update expert privacy settings
      const response = await fetch('/api/expert/update-privacy-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(privacySettings)
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      const result = await response.json();
      
      if (result.success) {
        showSuccessAlertMessage("Privacy settings updated successfully");
      } else {
        throw new Error(result.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      showErrorAlertMessage("Failed to save settings. Please try again.");
    } finally {
      setSavingPrivacy(false);
    }
  };

  // Render checkbox setting
  const renderCheckboxSetting = (settingKey, label, description) => {
    const isChecked = privacySettings[settingKey];

    return (
      <div className="setting-item-apss">
        <div className="setting-details-apss">
          <div className="setting-label-apss">{label}</div>
          <div className="setting-description-apss">{description}</div>
        </div>
        <button
          className="toggle-button-apss"
          onClick={() => toggleSetting(settingKey)}
          disabled={savingPrivacy}
        >
          {isChecked ? (
            <CheckSquare color={"#0e4410"} size={20} />
          ) : (
            <Square color={"#0e4410"} size={20} />
          )}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="privacy-security-container-apss">
        <div className="loading-container-apss">
          <div className="loading-spinner-apss"></div>
          <p>Loading privacy settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="privacy-security-container-apss">
        <div className="error-container-apss">
          <p className="error-message-apss">{error}</p>
          <button onClick={fetchPrivacySettings} className="retry-button-apss">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-security-container-apss">
      <div className="privacy-security-header-apss">
        <button
          className="back-button-apss"
          onClick={() => window.history.back()}
        >
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="privacy-security-title-apss">Privacy & Security</h1>
      </div>

      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="success-alert-right-apss">
          <div className="success-alert-content-apss">
            <div className="success-alert-icon-apss">✓</div>
            <div className="success-alert-message-apss">{successMessage}</div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <div className="error-alert-right-apss">
          <div className="error-alert-content-apss">
            <div className="error-alert-icon-apss">!</div>
            <div className="error-alert-message-apss">{errorMessage}</div>
          </div>
        </div>
      )}

      {/* Privacy Section */}
      <div className="settings-section-apss">
        <div className="section-header-apss">
          <Eye size={24} />
          <h3 className="section-title-apss">Profile Privacy</h3>
        </div>
        <div className="section-content-apss">
          <div className="setting-item-apss">
            <div className="setting-details-apss">
              <div className="setting-label-apss">Profile Visibility</div>
              <div className="setting-description-apss">
                Control who can see your expert profile information
              </div>
            </div>
            <select
              className="visibility-select-apss"
              value={privacySettings.profile_visibility}
              onChange={handleVisibilityChange}
              disabled={savingPrivacy}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="verified_only">Verified Users Only</option>
            </select>
          </div>

          {renderCheckboxSetting(
            "show_email",
            "Show Email",
            "Make your email visible to potential clients"
          )}

          {renderCheckboxSetting(
            "show_phone",
            "Show Phone Number",
            "Make your phone number visible to potential clients"
          )}

          {renderCheckboxSetting(
            "show_consultation_fee",
            "Show Consultation Fee",
            "Display your consultation fee on your profile"
          )}

          {renderCheckboxSetting(
            "show_availability",
            "Show Availability",
            "Display your availability status to clients"
          )}
        </div>
      </div>

      {/* Security Section */}
      <div className="settings-section-apss">
        <div className="section-header-apss">
          <Shield size={24} />
          <h3 className="section-title-apss">Security Settings</h3>
        </div>
        <div className="section-content-apss">
          {renderCheckboxSetting(
            "two_factor_authentication",
            "Two-Factor Authentication",
            "Add an extra layer of security to your account"
          )}

          {renderCheckboxSetting(
            "login_notifications",
            "Login Notifications",
            "Get notified of new login activities"
          )}

          {renderCheckboxSetting(
            "data_encryption",
            "Data Encryption",
            "Enable end-to-end encryption for your data"
          )}
        </div>
      </div>

      {/* Notification Section */}
      <div className="settings-section-apss">
        <div className="section-header-apss">
          <Bell size={24} />
          <h3 className="section-title-apss">Notification Settings</h3>
        </div>
        <div className="section-content-apss">
          {renderCheckboxSetting(
            "consultation_notifications",
            "Consultation Notifications",
            "Get notified about new consultation requests"
          )}

          {renderCheckboxSetting(
            "login_notifications",
            "Login Notifications",
            "Get notified of new login activities"
          )}
        </div>
      </div>

      {/* Save Privacy Settings Button */}
      <div className="save-changes-container-apss">
        <button
          className="save-all-changes-button-apss"
          onClick={saveAllSettings}
          disabled={savingPrivacy}
        >
          {savingPrivacy ? 'Saving...' : 'Save Privacy Settings'}
        </button>
      </div>

      {/* Password Section */}
      <div className="settings-section-apss">
        <div className="section-header-apss">
          <Lock size={24} />
          <h3 className="section-title-apss">Change Password</h3>
        </div>
        <div className="section-content-apss">
          <form className="password-form-apss" onSubmit={handlePasswordSubmit}>
            <div className="password-field-apss">
              <label className="password-label-apss">Current Password</label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="password-input-apss"
                disabled={savingPassword}
              />
            </div>
            <div className="password-field-apss">
              <label className="password-label-apss">New Password</label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="password-input-apss"
                disabled={savingPassword}
              />
            </div>
            <div className="password-field-apss">
              <label className="password-label-apss">
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className="password-input-apss"
                disabled={savingPassword}
              />
            </div>
            <button 
              type="submit" 
              className="change-password-button-apss"
              disabled={savingPassword}
            >
              {savingPassword ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
